/**
 * Reusable DTMF keypad component.
 * Used by both the Dialer tab (for typing numbers) and
 * the in-call keypad overlay (for sending DTMF tones).
 */

import { useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';

const KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
] as const;

const SUB_LABELS: Record<string, string> = {
  '1': '',
  '2': 'ABC',
  '3': 'DEF',
  '4': 'GHI',
  '5': 'JKL',
  '6': 'MNO',
  '7': 'PQRS',
  '8': 'TUV',
  '9': 'WXYZ',
  '*': '',
  '0': '+',
  '#': '',
};

interface KeypadProps {
  onDigit: (digit: string) => void;
  /** Enable keyboard input (digits, *, #) */
  enableKeyboard?: boolean;
  /** Compact mode for in-call overlay */
  compact?: boolean;
  /** Disable all keypad buttons */
  disabled?: boolean;
  className?: string;
}

export function Keypad({
  onDigit,
  enableKeyboard = true,
  compact = false,
  disabled = false,
  className,
}: KeypadProps) {
  // Keyboard listener
  useEffect(() => {
    if (!enableKeyboard || disabled) return;

    const handler = (e: KeyboardEvent) => {
      if (/^[0-9*#]$/.test(e.key)) {
        onDigit(e.key);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enableKeyboard, disabled, onDigit]);

  const handleClick = useCallback(
    (digit: string) => {
      onDigit(digit);
    },
    [onDigit]
  );

  return (
    <div className={cn('grid grid-cols-3', compact ? 'gap-2' : 'gap-2', className)}>
      {KEYS.flat().map((digit) => (
        <button
          key={digit}
          type="button"
          onClick={() => handleClick(digit)}
          disabled={disabled}
          className={cn(
            'flex flex-col items-center justify-center rounded-full transition-colors select-none',
            disabled
              ? 'bg-muted/30 text-muted-foreground/40 cursor-not-allowed'
              : 'bg-muted/50 hover:bg-muted active:bg-muted/80 text-foreground',
            compact ? 'h-11 w-11' : 'h-12 w-12'
          )}
        >
          <span className={cn('font-medium leading-none', compact ? 'text-lg' : 'text-xl')}>
            {digit}
          </span>
          {SUB_LABELS[digit] && (
            <span className={cn('text-muted-foreground mt-0.5 tracking-widest', compact ? 'text-[7px]' : 'text-[8px]')}>
              {SUB_LABELS[digit]}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
